import { useSelector, useDispatch } from 'react-redux';
import {
  useEffect, useContext, useState, ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Box from '@mui/material/Box';
import Chip, { ChipProps } from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';

import setDiffsHelper from '#utils/set-diffs-helper';
import type { Diff } from '#utils/order-diff-utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetOrdersQuery,
} from '#services/order';
import {
  useGetUserMutation,
  useUpdateUserMutation,
} from '#services/user';
import {
  formatDate,
  formatCurrency,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import {
  FULFILLMENT_LITERAL,
  FULFILLMENT_COLOR,
  FULFILLMENT_STATUS,
} from '#common/constants';
import {
  PAYMENT_COLOR,
} from '#common/constants/literal-map';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import DropdownFilterBox from '#components/DropdownFilterBox';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';

import {
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
} from './order.slice';

interface AuthUser {
  id?: string | number;
  isAdmin?: boolean;
  isSalesperson?: boolean;
  isCustomer?: boolean;
  isStaff?: boolean;
}

interface OrderListSlice {
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  selected: Array<string | number | undefined>;
}

interface UserDetailData {
  id?: string | number;
  username?: string;
  inactivated?: boolean;
  isCustomer?: boolean;
  totalAmounts?: number;
  totalOrders?: number;
  createdDate?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  disableShippingInfo?: boolean;
  shippingName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  shippingAddress?: string;
  disableBillingInfo?: boolean;
  billingName?: string;
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: string;
  notes?: string;
  userIndex?: number;
  total?: number;
  [key: string]: unknown;
}

interface OrderRow {
  id?: string | number;
  createdAsUserName?: string;
  createdAsUserCode?: string;
  createdDate?: string | number;
  linkedBulkOrderIds?: Array<string | number>;
  items: Array<{ fromInventory?: boolean; amounts?: number | string }>;
  receivings: Array<string | number>;
  fulfillmentStatus: number;
  totalPrice?: number;
  tags: Array<string | number>;
  shipableItemCount?: number;
  [key: string]: unknown;
}

export default function UserDetail() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state: { auth: { userInfo: AuthUser } }) => state.auth.userInfo);

  const {
    showBackdrop,
    hideBackdrop,

    showModal,
    hideModal,
    setModalData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const filterKey = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.filterKey);
  const filter = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.filter);
  const headerFilters = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.headerFilters);
  const order = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.order);
  const orderBy = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.orderBy);
  const pageIndex = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.pageIndex);
  const pageSize = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.pageSize);
  const rows = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.rows);
  const rowCount = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.rowCount);
  const selected = useSelector((state: { orderListForUserDetail: OrderListSlice }) => state.orderListForUserDetail.selected);
  const [debouncedFilter] = useDebounce(filter, 300);
  const [user, setUser] = useState<UserDetailData | null>(null);
  const [diffs, setDiffs] = useState<Diff[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [getUser] = useGetUserMutation();
  const [updateUser] = useUpdateUserMutation();

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  const query = useGetOrdersQuery({
    order,
    orderBy,
    filterKey,
    filter: debouncedFilter,
    headerFilters,
    pageIndex,
    pageSize,
    userId: urlParams.values.userId,
  }, {
    refetchOnMountOrArgChange: true,
    skip: !user, // Don't run query until user data is loaded
  });

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  useEffect(() => {
    if (user && !query.isLoading && !query.isFetching) {
      hideBackdrop();
      setIsInitialLoad(false);
    }
  }, [query.isLoading, query.isFetching, user]);

  useEffect(() => {
    showBackdrop();
    setIsInitialLoad(true);
    getUser(urlParams.values)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        if (hasError) {
          hideBackdrop();
          setIsInitialLoad(false);
          return;
        }

        const data = res.data as UserDetailData;
        setUser(data);

        const userId = data.id;
        if (urlParams.values.userId !== userId) {
          urlParams.set('userId', String(data.id));
          const search = urlParams.toString();
          void navigate({
            pathname: '/management/user/detail',
            search,
          }, { replace: true });
        }
        // Note: hideBackdrop() is now handled in separate useEffect
        // that waits for both user data and orders query to complete
      })
      .catch(() => {
        hideBackdrop();
        setIsInitialLoad(false);
      });
  }, [location.key]);

  if (isInitialLoad || !user) {
    return null;
  }

  const handleSaveUser = () => {
    showBackdrop();
    void updateUser({
      userId: user.id,
      diffs,
    }).then((res) => {
      simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Note has been saved');
    }).finally(() => {
      hideBackdrop();
    });
  };

  const handleUpdateActivation = () => {
    const inactivated = !user.inactivated;

    const _diffs = [{
      event: 'set',
      scope: 'inactivated',
      data: inactivated,
    }];

    void updateUser({
      userId: user.id,
      diffs: _diffs,
    }).then((res) => {
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `User is ${inactivated ? 'inactivated' : 'activated'}`);
      if (hasError) {
        return;
      }

      setUser({ ...user, inactivated });
    }).finally(() => {
      hideBackdrop();
    });
  };

  const gotoEditUser = () => {
    void navigate(`/management/user/edit?userId=${user.id}`);
  };

  const handleOrdersExport = () => {
    // WORKAROUND react state cannot work, use local variable instead
    let exportTargetOption = 'current';

    setModalData({
      children: (
        <>
          <Typography variant="h6" gutterBottom>
            Export Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl sx={{ mb: 5 }}>
            <FormLabel id="label-export-radio">Export</FormLabel>
            <RadioGroup
              aria-labelledby="label-export-radio"
              defaultValue="current"
              name="radio-buttons-group"
              onChange={(event, value) => {
                exportTargetOption = value;
              }}
            >
              <FormControlLabel value="current" control={<Radio />} label="Current page" />
              <FormControlLabel value="all" control={<Radio />} label="All orders" />
              <FormControlLabel
                disabled={!selected.length}
                value="selected"
                control={<Radio />}
                label={`Selected: ${selected.length} orders`}
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let additionalParam = `userId=${String(urlParams.values.userId)}`;
                if (exportTargetOption === 'current') {
                  additionalParam = `${additionalParam}&order=${order}&orderBy=${orderBy}&filter=${filter}&page=${pageIndex}&pageSize=${pageSize}`;
                } else if (exportTargetOption === 'all') {
                  additionalParam = `${additionalParam}&order=${order}&orderBy=${orderBy}&filter=${filter}&all=1`;
                } else if (exportTargetOption === 'selected') {
                  additionalParam = `${additionalParam}&orderIds=${selected.join(',')}`;
                }

                const exportUrl = `/api/export/orders?${additionalParam}`;
                window.open(exportUrl, '_blank');
                hideModal();
              }}
            >
              Export orders
            </Button>
          </Stack>
        </>
      ),
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
      },
    });
    showModal();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Order #',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      const url = `/management/order/detail?orderId=${o.id}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
          sx={{ textDecoration: 'inherit' }}
        >
          {o.id}
        </Link>
      );
    },
  }, {
    id: 'createdAsUserName',
    label: 'Store Name',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      return `${o.createdAsUserName}#${o.createdAsUserCode}`;
    },
  }, {
    id: 'createdDate',
    label: 'Order Date',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      try {
        const { createdDate } = o;
        return formatDate(createdDate as string);
      } catch (_err) {
        return 'UNAVAILABLE';
      }
    },
  }, {
    id: 'bulkOrder',
    label: 'Bulk Order',
    sortable: false,
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      const { linkedBulkOrderIds } = o;

      const allFromInventory = o.items.every((itm) => itm.fromInventory);
      if (allFromInventory) {
        return <Chip label="Inventory Order" color="warning" size="small" />;
      }

      if (!linkedBulkOrderIds) {
        return null;
      }
      return linkedBulkOrderIds.map((bulkOrderId) => {
        const url = `/management/bulk/detail?bulkOrderId=${bulkOrderId}`;
        return (
          <Link
            key={bulkOrderId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {bulkOrderId}
          </Link>
        );
      });
    },
  }, {
    id: 'receivings',
    label: 'Receiving List',
    sortable: false,
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      const { receivings } = o;
      return receivings.map((receivingId) => {
        const url = `/management/receiving/edit?receivingId=${receivingId}`;
        return (
          <Link
            key={receivingId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {receivingId}
          </Link>
        );
      });
    },
  }, {
    id: 'fulfillmentStatus',
    label: 'Fulfillment Status',
    filters: FULFILLMENT_LITERAL,
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      return (
        <Chip
          size="small"
          label={FULFILLMENT_LITERAL[o.fulfillmentStatus]}
          color={FULFILLMENT_COLOR[o.fulfillmentStatus] as ChipProps['color']}
        />
      );
    },
  }, {
    id: 'totalPrice',
    label: 'Total',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      return (o.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? `${formatCurrency(0)}`
        : `${formatCurrency(o.totalPrice as number)}`;
    },
  }, {
    id: 'itemCount',
    label: 'Items',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      return (o.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? 0
        : (o.items.reduce((total, itm) => (total + Number(itm.amounts)), 0));
    },
  }, {
    id: 'tags',
    label: 'Tags',
    renderer: (row) => (row as unknown as OrderRow).tags.join(','),
  }, {
    id: 'shipableItemCount',
    label: 'Shipping',
    renderer: (row) => {
      const o = row as unknown as OrderRow;
      if (o.shipableItemCount
        && o.fulfillmentStatus !== FULFILLMENT_STATUS.CANCELED) {
        return (
          <Chip
            size="small"
            label={`New In Warehouse (${o.shipableItemCount})`}
            color={PAYMENT_COLOR[2] as ChipProps['color']}
          />
        );
      }
      return '-';
    },
  // }, {
  //   id: 'paymentStatus',
  //   label: 'Payment Status',
  //   renderer: (o) => (
  //     <Chip
  //       size="small"
  //       label={PAYMENT_STATUS[o.paymentStatus]}
  //       color={PAYMENT_COLOR[o.paymentStatus]}
  //     />
  //   ),
  }];

  const tableProps: EnhancedTableProps = {
    name: 'order-list-admin',

    headers: tableHeaders,

    pinnedColumns: {
      left: ['id'],
    },

    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    getRowStyle: (row) => (
      ((row as unknown as OrderRow).fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? { textDecoration: 'line-through' }
        : {}
    ),

    hasSelectColumn: true,
    selected,
    setSelected,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,
    rows,
  };

  const handleTravelUser = (direction: number) => {
    urlParams.set('userIndex', (val) => String(Number(val) + direction));
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/user/detail',
      search,
    });
  };

  let toolBar: ReactNode = null;
  if (urlParams.values.userIndex) {
    toolBar = (
      <TitleBar
        title="User Detail"
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelUser(direction)}
          itemIndex={user.userIndex as number}
          itemSize={user.total as number}
        />
      </TitleBar>
    );
  }

  return (
    <>
      {toolBar}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant={'h7' as TypographyProps['variant']}>
          {user.username}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 600,
            bgcolor: user.inactivated ? 'warning.main' : 'success.main',
            color: user.inactivated ? 'warning.contrastText' : 'success.contrastText',
            lineHeight: 1.5,
          }}
        >
          {user.inactivated ? 'Inactive' : 'Active'}
        </Typography>
      </Box>
      <Paper
        sx={{
          px: 2,
          mt: 2,
          '& .MuiGrid-root': { mb: 2 },
        }}
      >
        <Box sx={{ typography: 'subtitle2' }}>
          <Grid container>
            {(user.isCustomer && (currentUser.isAdmin || currentUser.isSalesperson)) && (
              <>
                <Grid size={4}>
                  Amount Spent
                </Grid>
                <Grid size={8}>
                  {formatCurrency(user.totalAmounts as number)}
                </Grid>

                <Grid size={4}>
                  Orders Amount
                </Grid>
                <Grid size={8}>
                  {user.totalOrders}
                </Grid>

                <Grid size={4}>
                  Member Since
                </Grid>
                <Grid size={8}>
                  {new Date(user.createdDate as string).getFullYear()}
                </Grid>
              </>
            )}

            <Grid size={4}>
              Contact Info
            </Grid>
            <Grid size={8}>
              <Box>{user.name}</Box>
              <Box>{user.email}</Box>
              <Box>{user.phone}</Box>
              <Box>{user.address}</Box>
            </Grid>

            {
              !user.disableShippingInfo && (
                <>
                  <Grid size={4}>
                    Shipping Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{user.shippingName}</Box>
                    <Box>{user.shippingEmail}</Box>
                    <Box>{user.shippingPhone}</Box>
                    <Box>{user.shippingAddress}</Box>
                  </Grid>
                </>
              )
            }

            {
              !user.disableBillingInfo && (
                <>
                  <Grid size={4}>
                    Billing Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{user.billingName}</Box>
                    <Box>{user.billingEmail}</Box>
                    <Box>{user.billingPhone}</Box>
                    <Box>{user.billingAddress}</Box>
                  </Grid>
                </>
              )
            }

            <Grid size={12}>
              <Stack spacing={2} direction="row" sx={{
                alignItems: 'center',
              }}>
                <Button
                  variant="contained"
                  color={user.inactivated ? 'success' : 'error'}
                  onClick={handleUpdateActivation}
                >
                  {user.inactivated ? 'Activate' : 'Deactivate'}
                </Button>
                <Button
                  variant="contained"
                  onClick={gotoEditUser}
                >
                  Edit
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box
        sx={{ mb: 1 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Customer Orders
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <DropdownFilterBox
            width={500}
            dropdownItems={[{
              value: 'orderId',
              text: 'Order #',
            }, {
              value: 'poNumber',
              text: 'PO #',
            }, {
              value: 'style',
              text: 'Style #',
            }, {
              value: 'inventoryOrderId',
              text: 'Inventory #',
            }]}
            filter={filter}
            filterKey={filterKey}
            filterPlaceholder="Filter products"
            dispatch={dispatch as (action: unknown) => void}
            setFilter={setFilter}
            setFilterKey={setFilterKey}
            setFilterForQuery={(v) => dispatch(setFilter(v))}
          />

          <ButtonGroup variant="outlined" size="small">
            <Button
              onClick={() => { void navigate(`/management/customer/report?customerId=${user.id}`); }}
              color="secondary"
            >
              Customer Report
            </Button>
            <Button
              onClick={handleOrdersExport}
            >
              Export
            </Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ m: 2 }} />
        <EnhancedTable
          {...tableProps}
        />
      </Box>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Notes
          </Typography>
        </Box>
        <TextField
          multiline
          fullWidth
          rows={5}
          value={user.notes || ''}
          onChange={(event) => {
            const newNotes = event.target.value;
            setDiffsHelper('notes', newNotes, diffs, setDiffs);
            setUser({
              ...user,
              notes: newNotes,
            });
          }}
        />

        <Button
          variant="contained"
          onClick={handleSaveUser}
        >
          Save Notes
        </Button>
      </Box>
    </>
  );
}
